import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class MappingResultService {
  private readonly apiUrl = environment.apiUrl;
  private extractionTrigger$ = new Subject<{
    doc_run_id: string;
    extractionTriggeredByUpdateResults?: boolean;
    request_id?: string;
  }>();
  public extractionTrigger: Observable<{
    doc_run_id: string;
    extractionTriggeredByUpdateResults?: boolean;
    request_id?: string;
  }> = this.extractionTrigger$.asObservable();

  // Broadcast when a NEW request_id becomes active for a given doc_run_id (fresh extraction or rerun)
  private requestIdUpdates$ = new Subject<{
    doc_run_id: string;
    request_id: string;
  }>();
  public requestIdUpdates: Observable<{
    doc_run_id: string;
    request_id: string;
  }> = this.requestIdUpdates$.asObservable();

  private latestRequestIds: { [doc_run_id: string]: string } = {};
  private extractionCompleted$ = new Subject<string>();
  public extractionCompleted: Observable<string> =
    this.extractionCompleted$.asObservable();
  public notifyExtractionCompleted(doc_run_id: string) {
    try {
      if (doc_run_id) this.extractionCompleted$.next(doc_run_id);
    } catch (e) {}
  }

  private fileLocation$ = new Subject<string>();
  fileLocation: Observable<string> = this.fileLocation$.asObservable();
  private fileLocationValue: string = '';

  constructor(private http: HttpClient) {}

  getSectionInfo(params: {
    extraction_run_id: string;
    section_name: string;
    tab_name: string;
    sub_product: string;
  }): Observable<any> {
    const url = `${this.apiUrl}loan-review/extraction-report/section-info`;
    let sanitizedSectionName = params.section_name || '';
    sanitizedSectionName = sanitizedSectionName
      .replace(/\//g, ' ')
      .replace(/:/g, '')
      .replace(/;/g, '')
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .replace(/ +/g, ' ')
      .trim();
    const httpParams = new HttpParams()
      .set('extraction_run_id', params.extraction_run_id)
      .set('section_name', sanitizedSectionName)
      .set('tab_name', params.tab_name)
      .set('sub_product', params.sub_product);
    return this.http
      .get(url, { params: httpParams, responseType: 'text' })
      .pipe(map((response: string) => ({ section_markdown: response })));
  }

  getFeedbackCategoryById(categoryId: string): Observable<any> {
    const url = `${this.apiUrl}loan-review/feedback-category/category/${categoryId}`;
    return this.http.get<any>(url);
  }

  displayPdf(url: string) {
    this.fileLocationValue = url;
    this.fileLocation$.next(this.fileLocationValue);
  }

  updateAttributeValidation(payload: {
    doc_run_id: string;
    request_id: string;
    data_element_name: string;
    updated_confidence_validation?: boolean;
    previous_confidence_validation?: boolean | null;
    updated_by?: string;
    category_name?: string;
    comments?: string[];
    ai_answer: string;
  }): Observable<any> {
    const url = `${this.apiUrl}loan-review/update-attribute-validation`;
    return this.http.put(url, payload);
  }

  feedbackCategoryComment(payload: {
    doc_run_id: string;
    data_element_name: string;
    category_name?: string;
    comments?: string[];
    updated_by: string;
  }): Observable<any> {
    const url = `${this.apiUrl}loan-review/feedback/audit-log/request`;
    return this.http.post(url, payload);
  }

  feedbackCommentPost(payload: {
    doc_run_id: string;
    data_element_name: string;
    comments: string[];
  }): Observable<{
    count: number;
    comments: {
      audit_log: { updated_by: string; updated_at: string; comment: string[] };
    }[];
  }> {
    const url = `${this.apiUrl}loan-review/feedback/audit-log/request`;
    return this.http.post<{
      count: number;
      comments: {
        audit_log: {
          updated_by: string;
          updated_at: string;
          comment: string[];
        };
      }[];
    }>(url, payload);
  }

  getMappingResult(doc_run_id: string, request_id: string) {
    let params = {
      doc_run_id: doc_run_id,
      request_id: request_id,
    };
    return this.http.post<any>(
      this.apiUrl + 'loan-review/get-run-results',
      params
    );
  }

  getHistoryAndComments(payload: {
    doc_run_id: string;
    request_id: string;
    data_element_name: string;
  }): Observable<any> {
    const url = `${this.apiUrl}loan-review/feedback/history/request`;
    const params = new HttpParams()
      .set('request_id', payload.request_id)
      .set('doc_run_id', payload.doc_run_id)
      .set('data_element_name', payload.data_element_name);
    return this.http.get<{
      count: number;
      comments: Array<{
        id: string;
        comment: string;
        updated_at: string;
        updated_by: string;
        category_name: string;
      }>;
    }>(url, { params });
  }

  getCommentsCount(
    doc_run_id: string
  ): Observable<{ [data_element_name: string]: number }> {
    const url = `${
      this.apiUrl
    }loan-review/feedback/comments-count/request?doc_run_id=${encodeURIComponent(
      doc_run_id
    )}`;
    return this.http.get<{ [data_element_name: string]: number }>(url, {});
  }

  getFeedbackSummaryRequest(
    request_id: string,
    doc_run_id: string
  ): Observable<any> {
    const url = `${
      this.apiUrl
    }loan-review/feedback/summary/request?request_id=${encodeURIComponent(
      request_id
    )}&doc_run_id=${encodeURIComponent(doc_run_id)}`;
    return this.http.get<any>(url).pipe(
      map((res: any) => ({
        total_data_elements: res?.total_data_elements ?? 0,
        total_positives: res?.total_positives ?? 0,
        user_review_count: res?.user_review_count ?? 0,
      }))
    );
  }

  getPdf(
    sasurl: string,
    bbox: any,
    page_no: number,
    doc_id: string
  ): Observable<Blob> {
    let params = {
      sas_url: sasurl,
      bounding_box: bbox,
      page_number: page_no,
    };

    const body = { doc_id, ...params };
    return this.http
      .post(this.apiUrl + 'loan-review/docs/highlight', body, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(map((response: any) => response.body as Blob));
  }

  getSourceDoc(doc_id: string) {
    const body = { doc_id };
    return this.http.post(this.apiUrl + 'loan-review/docs/display', body, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  generateReport(payload: {
    extraction_run_id: string;
    sub_product: string;
    tab_name: string;
  }): Observable<any> {
    const url = `${this.apiUrl}loan-review/extraction-report/generate`;
    return this.http.post(url, payload);
  }

  getTabNames(subProduct: string): Observable<string[]> {
    const url = `${
      this.apiUrl
    }loan-review/extraction-report/get-tab-names?sub_product=${encodeURIComponent(
      subProduct
    )}`;
    return this.http.get<string[]>(url);
  }

  getAttributeValidation(params: {
    doc_run_id: string;
    request_id: string;
    tab_name: string; // 'ALL' for all tabs or specific tab name
  }): Observable<any> {
    const url = `${this.apiUrl}loan-review/get-attribute-validation`;
    const httpParams = new HttpParams()
      .set('doc_run_id', params.doc_run_id)
      .set('request_id', params.request_id)
      .set('tab_name', params.tab_name);
    return this.http.get<any>(url, { params: httpParams });
  }

  validationUpdate(payload: {
    doc_run_id: string;
    request_id: string; 
    data_element_name: string;
    validation: 'TAPE' | 'SOURCE' | null;
    value?: string;
  }): Observable<any> {
    const url = `${this.apiUrl}loan-review/update-answer-validation`;
    return this.http.put(url, payload);
  }

  bulkUpdate(payload: any): Observable<any> {
    return this.http.post(
      this.apiUrl + 'loan-review/update-audit-log',
      payload
    );
  }

  getDependentAttributesResult(payload: {
    doc_run_id: string;
    request_id: string;
    attributes: string[];
  }): Observable<any> {
    const url = `${this.apiUrl}loan-review/get-dependent-data-elements-run-results`;
    return this.http.post<any>(url, payload);
  }

  uploadDocument(formData: FormData): Observable<any> {
    const body = new FormData();
    body.append('file', formData.get('document') as Blob);
    body.append('request_id', formData.get('request_id') as string);
    body.append(
      'extraction_run_id',
      formData.get('extraction_run_id') as string
    );
    body.append('sub_product', formData.get('sub_product') as string);
    body.append('tab_name', formData.get('tab_name') as string);

    return this.http.post(
      this.apiUrl + 'loan-review/extraction-report/generate',
      body
    );
  }
  getEngagementId(): any {
    let tenantId = localStorage.getItem('tenantId');
    return tenantId || '';
  }

  updateReRunResults(payload: {
    request_id: string;
    doc_run_id: string;
    extraction_run_id: string;
    data_elements: string[];
  }): Observable<any> {
    const url = `${this.apiUrl}loan-review/extraction/re-run`;
    const updatedPayload = {
      ...payload,
      engagement_id: this.getEngagementId(),
      is_latest: true,
    };
    return this.http.put<any>(url, updatedPayload).pipe(
      map((res) => {
        try {
          if (updatedPayload && updatedPayload.doc_run_id) {
            const requestId =
              res && (res.request_id || res.requestId)
                ? res.request_id || res.requestId
                : undefined;
            this.extractionTrigger$.next({
              doc_run_id: updatedPayload.doc_run_id,
              extractionTriggeredByUpdateResults: true,
              request_id: requestId,
            });
            if (requestId) {
              // Also announce the new request id so mapping-result can immediately switch
              this.requestIdUpdates$.next({ doc_run_id: updatedPayload.doc_run_id, request_id: requestId });
              this.latestRequestIds[updatedPayload.doc_run_id] = requestId;
            }
          }
        } catch (e) {
          // ignore
        }
        return res;
      })
    );
  }

  // Method for other components (e.g., view-details) to announce a fresh request id when extraction starts
  public announceRequestId(doc_run_id: string, request_id: string) {
    if (doc_run_id && request_id) {
      this.requestIdUpdates$.next({ doc_run_id, request_id });
      this.latestRequestIds[doc_run_id] = request_id;
    }
  }

  // Allow users to query the last known request id for a doc_run_id (handles late subscriptions)
  public getLatestRequestId(doc_run_id: string): string | undefined {
    return this.latestRequestIds[doc_run_id];
  }
}
